import { lookupOfficialVillageGeo } from '../data/master/villages';
import { lookupPinForVillage } from './pinLookup';

function isUsablePolygon(geojson) {
  return (
    geojson &&
    (geojson.type === 'Polygon' || geojson.type === 'MultiPolygon') &&
    Array.isArray(geojson.coordinates)
  );
}

function withPin(result, villageName) {
  const pin =
    lookupPinForVillage(villageName, {
      latitude: result.latitude,
      longitude: result.longitude,
    }) || null;
  return { ...result, pin };
}

/**
 * Resolve village map location.
 * 1) Official Kachchh boundary import (preferred)
 * 2) OpenStreetMap Nominatim fallback
 * Also attaches PIN from India Post directory when possible.
 */
export async function searchVillageOnMap({
  villageName,
  talukaName = 'Mundra',
  district = 'Kutch',
  state = 'Gujarat',
  country = 'India',
}) {
  const official = lookupOfficialVillageGeo(villageName, talukaName);
  if (official?.boundary) {
    return withPin(
      {
        latitude: official.latitude,
        longitude: official.longitude,
        displayName: `${official.sourceName}, ${official.taluka}, Kachchh, Gujarat`,
        boundary: official.boundary,
        source: 'official',
        boundingBox: null,
      },
      villageName,
    );
  }

  const byNameOnly = lookupOfficialVillageGeo(villageName);
  if (byNameOnly?.boundary) {
    return withPin(
      {
        latitude: byNameOnly.latitude,
        longitude: byNameOnly.longitude,
        displayName: `${byNameOnly.sourceName}, ${byNameOnly.taluka}, Kachchh, Gujarat`,
        boundary: byNameOnly.boundary,
        source: 'official',
        boundingBox: null,
      },
      villageName,
    );
  }

  const q = [villageName, talukaName, district, state, country]
    .filter(Boolean)
    .join(', ');

  const url = new URL('https://nominatim.openstreetmap.org/search');
  url.searchParams.set('q', q);
  url.searchParams.set('format', 'json');
  url.searchParams.set('limit', '5');
  url.searchParams.set('polygon_geojson', '1');
  url.searchParams.set('addressdetails', '1');
  url.searchParams.set('countrycodes', 'in');

  const res = await fetch(url.toString(), {
    headers: {
      Accept: 'application/json',
      'User-Agent': 'UtkarshAdmin/0.1 (village-master)',
    },
  });

  if (!res.ok) {
    throw new Error('Map search failed');
  }

  const data = await res.json();
  if (!data?.length) {
    return null;
  }

  const hit = data.find((h) => isUsablePolygon(h.geojson)) || data[0];
  const base = withPin(
    {
      latitude: Number(hit.lat),
      longitude: Number(hit.lon),
      displayName: hit.display_name,
      boundary: isUsablePolygon(hit.geojson) ? hit.geojson : null,
      source: 'nominatim',
      boundingBox: hit.boundingbox
        ? {
            south: Number(hit.boundingbox[0]),
            north: Number(hit.boundingbox[1]),
            west: Number(hit.boundingbox[2]),
            east: Number(hit.boundingbox[3]),
          }
        : null,
    },
    villageName,
  );
  const fromAddress = hit.address?.postcode ? String(hit.address.postcode) : null;
  return { ...base, pin: base.pin || fromAddress };
}

/** Rough dashed border when no official polygon exists. */
export function approximateBoundaryRing(lat, lng, radiusKm = 0.8, points = 48) {
  const ring = [];
  const latRad = (lat * Math.PI) / 180;
  for (let i = 0; i <= points; i += 1) {
    const angle = (i / points) * Math.PI * 2;
    const dLat = (radiusKm / 111.32) * Math.cos(angle);
    const dLng = (radiusKm / (111.32 * Math.cos(latRad))) * Math.sin(angle);
    ring.push([lat + dLat, lng + dLng]);
  }
  return ring;
}

/**
 * Reverse-geocode a map pin via OpenStreetMap Nominatim.
 * Used when the center pin moves / is tapped (admin select-on-map).
 */
export async function reverseGeocode(lat, lng) {
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    throw new Error('Invalid map coordinates');
  }

  const url = new URL('https://nominatim.openstreetmap.org/reverse');
  url.searchParams.set('lat', String(latitude));
  url.searchParams.set('lon', String(longitude));
  url.searchParams.set('format', 'json');
  url.searchParams.set('addressdetails', '1');
  url.searchParams.set('zoom', '18');

  const res = await fetch(url.toString(), {
    headers: {
      Accept: 'application/json',
      'User-Agent': 'UtkarshAdmin/0.1 (map-pin-reverse)',
    },
  });

  if (!res.ok) {
    throw new Error('Could not resolve that map pin');
  }

  const data = await res.json();
  const a = data?.address || {};

  const house = [a.house_number, a.building, a.amenity]
    .filter(Boolean)
    .join(' ')
    .trim();
  const street = [a.road, a.pedestrian, a.path, a.residential]
    .filter(Boolean)
    .join(', ')
    .trim();
  const locality =
    a.village ||
    a.hamlet ||
    a.suburb ||
    a.neighbourhood ||
    a.locality ||
    a.town ||
    a.city ||
    '';
  const taluka = a.county || a.municipality || a.city_district || '';
  const district =
    a.state_district || a.district || a.county || a.city || '';
  const state = a.state || '';
  const pin = a.postcode ? String(a.postcode).replace(/\D/g, '').slice(0, 6) : '';

  return {
    address1: house,
    address2: street,
    locality: String(locality || '').trim(),
    taluka: String(taluka || '').trim(),
    district: String(district || '').trim(),
    state: String(state || '').trim(),
    country: a.country || 'India',
    pin,
    latitude,
    longitude,
    displayName: data?.display_name || '',
  };
}
